import type { GameState } from '../game/GameState.js';
import type { Color, PieceKind, Square } from '../game/types.js';
import { sameSquare } from '../game/types.js';
import { PIECE_RADIUS } from '../utils/constants.js';
import { squareToCanvasCenter, type ViewerRotation } from './geometry.js';
import { drawDarkDiscRim } from '@bn-games/render-helpers';
import type { RenderPalette } from './palette.js';

export type PieceSkip = Square | ((sq: Square) => boolean) | null;

export function drawPieces(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  palette: RenderPalette,
  rotation: ViewerRotation = 0,
  skip?: PieceSkip,
): void {
  const shouldSkip = (sq: Square): boolean => {
    if (!skip) return false;
    if (typeof skip === 'function') return skip(sq);
    return sameSquare(skip, sq);
  };
  for (const { square, piece } of state.pieces()) {
    if (shouldSkip(square)) continue;
    const { x, y } = squareToCanvasCenter(square, rotation);
    drawPiece(ctx, x, y, piece.color, piece.kind, palette);
  }
}

export interface DrawPieceOptions {
  readonly scale?: number;
  readonly alpha?: number;
  readonly shadowLift?: number; // pixels — extra drop shadow when airborne
}

export function drawPiece(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: Color,
  kind: PieceKind,
  palette: RenderPalette,
  options: DrawPieceOptions = {},
): void {
  const scale = options.scale ?? 1;
  const alpha = options.alpha ?? 1;
  const shadowLift = options.shadowLift ?? 0;

  ctx.save();
  if (alpha < 1) ctx.globalAlpha = alpha;
  if (scale !== 1) {
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.translate(-x, -y);
  }
  drawPieceShadow(ctx, x, y, shadowLift);
  drawPieceBody(ctx, x, y, color, palette);
  if (kind === 'king') drawCrown(ctx, x, y, color, palette);
  ctx.restore();
}

function drawPieceShadow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  lift: number,
): void {
  const r = PIECE_RADIUS;
  const offsetY = 2 + lift * 0.6;
  const radiusBoost = 1 + lift * 0.25;
  const alpha = lift > 0 ? 0.32 : 0.25;
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + 1, y + offsetY, r + radiusBoost, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
  ctx.fill();
  ctx.restore();
}

function drawPieceBody(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: Color,
  palette: RenderPalette,
): void {
  const isWhite = color === 'white';
  const base = isWhite ? palette.whitePieceBase : palette.blackPieceBase;
  const highlight = isWhite ? palette.whitePieceHighlight : palette.blackPieceHighlight;
  const shadow = isWhite ? palette.whitePieceShadow : palette.blackPieceShadow;
  const border = isWhite ? palette.whitePieceBorder : palette.blackPieceBorder;
  const r = PIECE_RADIUS;

  // Dome: radial gradient, highlight from upper-left.
  const dome = ctx.createRadialGradient(
    x - r * 0.3,
    y - r * 0.3,
    r * 0.1,
    x,
    y,
    r,
  );
  dome.addColorStop(0, highlight);
  dome.addColorStop(0.6, base);
  dome.addColorStop(1, shadow);

  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = dome;
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // A dark piece sits on a dark square, where its own border barely shows.
  if (!isWhite) drawDarkDiscRim(ctx, x, y, r);

  // Inner groove ring — gives the piece a turned-on-a-lathe profile.
  ctx.beginPath();
  ctx.arc(x, y, r * 0.62, 0, Math.PI * 2);
  ctx.strokeStyle = isWhite ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Top shine — clipped to the piece so the sheen never spills onto the square.
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.clip();
  const shine = ctx.createRadialGradient(
    x - r * 0.35,
    y - r * 0.35,
    0,
    x - r * 0.2,
    y - r * 0.2,
    r * 0.6,
  );
  shine.addColorStop(0, isWhite ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.16)');
  shine.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = shine;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawCrown(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: Color,
  palette: RenderPalette,
): void {
  const isWhite = color === 'white';
  const gold = isWhite ? palette.whiteCrownGold : palette.blackCrownGold;
  const deep = isWhite ? palette.whiteCrownDeep : palette.blackCrownDeep;

  const r = PIECE_RADIUS;
  const crownW = r * 1.15;
  const crownH = r * 0.62;
  const baseY = cy + crownH * 0.18;
  const leftX = cx - crownW / 2;
  const rightX = cx + crownW / 2;
  const bandH = crownH * 0.32;
  const peakY = baseY - crownH;
  const dipY = baseY - crownH * 0.45;
  const peakXs = [leftX, cx, rightX];
  const dipXs = [
    leftX + crownW * 0.25,
    leftX + crownW * 0.75,
  ];

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;

  // Spiked crown silhouette: three peaks with two dips between them, sitting
  // on a band running across the lower portion of the piece.
  ctx.beginPath();
  ctx.moveTo(leftX, baseY);
  ctx.lineTo(peakXs[0] ?? leftX, peakY);
  ctx.lineTo(dipXs[0] ?? cx, dipY);
  ctx.lineTo(peakXs[1] ?? cx, peakY);
  ctx.lineTo(dipXs[1] ?? cx, dipY);
  ctx.lineTo(peakXs[2] ?? rightX, peakY);
  ctx.lineTo(rightX, baseY);
  ctx.closePath();

  const grad = ctx.createLinearGradient(0, peakY, 0, baseY);
  grad.addColorStop(0, gold);
  grad.addColorStop(1, deep);
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.strokeStyle = deep;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Band — a darker stripe along the bottom of the crown for definition.
  ctx.fillStyle = deep;
  ctx.fillRect(leftX, baseY - bandH * 0.35, crownW, 1.2);

  // Gem dots: one on each peak.
  ctx.fillStyle = gold;
  for (const px of peakXs) {
    if (px === undefined) continue;
    ctx.beginPath();
    ctx.arc(px, peakY + 1.5, 1.4, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

